// Main Game Manager Class
class GameManager {
    // Helper function to format date and time in UK format
    formatUKDateTime() {
        const now = new Date();
        const dateOptions = { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
        };
        const timeOptions = { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: false 
        };
        
        const ukDate = now.toLocaleDateString('en-GB', dateOptions);
        const ukTime = now.toLocaleTimeString('en-GB', timeOptions);
        
        return `${ukDate} ${ukTime}`;
    }

    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        
        // Initialize managers
        this.audioManager = new AudioManager();
        this.movementManager = new MovementManager(canvas);
        this.bonusManager = new BonusManager();
        
        // Load character image
        this.characterImage = new Image();
        this.characterImage.src = '/images/character.png';
        this.characterImageLoaded = false;
        
        this.characterImage.onload = () => {
            this.characterImageLoaded = true;
            this.render(); // Re-render when image loads
        };
        
        // Update player position after canvas is properly sized
        this.movementManager.updatePlayerPosition();
        
        // Game state
        this.gameStarted = false;
        this.gameRunning = false;
        this.gameOver = false;
        this.gamePaused = false;
        this.showBonusTimer = false;
        
        // Game variables
        this.score = 0; // Only regular game points (for level progression)
        this.bonusScore = 0; // Bonus points (don't affect level progression)
        this.highScore = 0;
        this.level = 1;
        this.sizePercentage = GameConfig.CHARACTER.BASE_PERCENTAGE;
        this.characterSize = GameConfig.CHARACTER.BASE_SIZE;
        this.levelSkipped = false; // Track if player has used level skip
        
        // Bonus round tracking
        this.previousBonusRoundState = false; // Track bonus round status changes
        
        // Bomb system
        this.bombCount = 0; // Current number of bombs in inventory
        
        // Mushroom combo system
        this.lastMushroomTime = 0;
        this.mushroomCombo = 0;
        this.popupTexts = []; // Array to store popup text animations
        
        // Timing
        this.startTime = null;
        this.gameStartTime = null;
        this.bonusTimerEnd = null;
        this.lastGrowthTime = 0;
        this.bonusCountdownStart = null;
        
        // Game objects
        this.enemies = [];
        this.powerups = [];
        this.lastPowerupSpawn = 0;
        this.lastEnemySpawn = 0;
        this.lastMushroomSpawn = 0; // Track mushroom spawning timing
        
        // Bind methods
        this.gameLoop = this.gameLoop.bind(this);
        
        // Set up event listeners
        this.setupEventListeners();
        
        // Load high score
        this.loadHighScore();
        
        // Initialize Level 1 speed settings
        const level1Config = GameConfig.LEVEL_CONFIGS[1];
        this.movementManager.updateSpeedLimits(level1Config.playerSpeed.max, level1Config.playerSpeed.preGame);
        
        // Initialize UI
        this.updateUI();
        
        // Initial render to show player before game starts
        this.render();
        
        // Start pre-game loop for movement
        this.preGameLoop();
    }
    
    setupEventListeners() {
        // Canvas click to start
        this.canvas.addEventListener('click', (e) => {
            if (!this.gameStarted) {
                this.startCountdown();
            }
        });
        
        // Key handlers (delegate to movement manager)
        document.addEventListener('keydown', (e) => {
            // Handle game start with space bar
            if (e.key === ' ' && !this.gameStarted) {
                this.startCountdown();
                return;
            }
            
            // Handle restart key
            if (e.key.toLowerCase() === 'r') {
                this.restartGame();
                return;
            }
            
            // Handle pause key
            if (e.key.toLowerCase() === 'p') {
                this.togglePause();
                return;
            }
            
            this.movementManager.handleKeyDown(e, this);
        });
        
        document.addEventListener('keyup', (e) => {
            this.movementManager.handleKeyUp(e);
        });
    }
    
    startCountdown() {
        if (this.gameStarted) return;
        
        this.gameStarted = true;
        // Pass false for gameStarted (since game hasn't actually started yet) and provide callback to start background music
        this.audioManager.playSound(false, () => {
            this.audioManager.startBackgroundMusic();
        });
        
        let countdown = 3;
        this.updateInstructions(`Starting in ${countdown}...`);
        
        const countdownInterval = setInterval(() => {
            countdown--;
            if (countdown > 0) {
                this.updateInstructions(`Starting in ${countdown}...`);
            } else {
                clearInterval(countdownInterval);
                this.startGame();
            }
        }, 1000);
    }
    
    startGame() {
        this.gameRunning = true;
        this.gameStartTime = Date.now();
        this.startTime = Date.now();
        this.lastGrowthTime = Date.now();
        
        // Background music will start after click sound finishes (handled in startCountdown)
        this.updateInstructions(GameConfig.LEVEL_CONFIGS[this.level].message);
        
        // Start the game loop
        this.gameLoop();
    }
    
    gameLoop() {
        if (!this.gameRunning) return;
        
        // If paused, just render current state and continue loop
        if (this.gamePaused) {
            this.render();
            setTimeout(this.gameLoop, GameConfig.PHYSICS.FRAME_RATE);
            return;
        }
        
        const now = Date.now();
        
        // Update movement
        this.movementManager.updateMovement(this);
        
        // Handle growth
        this.handleGrowth(now);
        
        // Handle bonus system
        this.bonusManager.update(this, now);
        
        // Check for bonus round state changes and update instructions
        if (this.bonusManager.inBonusRound !== this.previousBonusRoundState) {
            this.previousBonusRoundState = this.bonusManager.inBonusRound;
            const currentLevel = GameConfig.LEVEL_CONFIGS[this.level];
            this.updateInstructions(currentLevel.message);
        }
        
        // Spawn enemies and powerups (only if not in bonus round)
        if (!this.bonusManager.inBonusRound) {
            this.spawnEnemies(now);
            this.spawnPowerups(now);
        }
        
        // Update game objects
        this.updateEnemies();
        this.updatePowerups();
        
        // Check collisions (always check for powerups, but skip enemy collisions during bonus round)
        this.checkCollisions();
        
        // Check for combo expiration
        this.checkComboExpiration(now);
        
        // Update level progression
        this.checkLevelProgression();
        
        // Render everything
        this.render();
        
        // Continue loop
        setTimeout(this.gameLoop, GameConfig.PHYSICS.FRAME_RATE);
    }
    
    preGameLoop() {
        // Allow movement before game starts and during countdown
        if (!this.gameRunning) {
            this.movementManager.updateMovement(this);
            this.render();
            setTimeout(() => this.preGameLoop(), GameConfig.PHYSICS.FRAME_RATE);
        }
    }
    
    handleGrowth(now) {
        if (now - this.lastGrowthTime >= GameConfig.CHARACTER.GROWTH_INTERVAL) {
            this.sizePercentage = Math.min(this.sizePercentage + 10, GameConfig.CHARACTER.MAX_PERCENTAGE);
            this.characterSize = Math.min(
                (GameConfig.CHARACTER.BASE_SIZE * this.sizePercentage) / GameConfig.CHARACTER.BASE_PERCENTAGE,
                GameConfig.CHARACTER.MAX_SIZE
            );
            this.lastGrowthTime = now;
            this.updateUI();
        }
    }
    
    useBomb() {
        if (this.bombCount <= 0) return;
        
        console.log('💥 Bomb used! Enemies in blast radius will be destroyed.');
        
        // Reduce bomb count
        this.bombCount--;
        
        // Calculate player position for explosion center
        const playerX = this.movementManager.playerX;
        const playerY = this.movementManager.playerY;
        const explosionRadius = GameConfig.POWERUPS.BOMB.EXPLOSION_RADIUS;
        
        // Create explosion visual effect with enemy destruction tracking
        this.createExplosion(playerX, playerY, explosionRadius);
        
        // Update UI to reflect new bomb count
        this.updateUI();
    }
    
    createExplosion(x, y, maxRadius) {
        // Create explosion object that tracks enemy destruction
        const explosion = {
            x: x,
            y: y,
            radius: 0,
            maxRadius: maxRadius,
            alpha: 1.0,
            startTime: Date.now(),
            duration: 500, // 0.5 seconds
            enemiesDestroyed: 0,
            totalBonusPoints: 0
        };
        
        // Add to explosions array
        if (!this.explosions) this.explosions = [];
        this.explosions.push(explosion);
    }
    
    spawnEnemies(now) {
        const config = GameConfig.LEVEL_CONFIGS[this.level];
        if (now - this.lastEnemySpawn > config.globalSpawnRate) {
            // Create weighted array of enemy types
            const weightedEnemies = [];
            Object.entries(config.enemies).forEach(([type, enemyConfig]) => {
                if (enemyConfig.enabled) {
                    for (let i = 0; i < enemyConfig.spawnWeight * 10; i++) {
                        weightedEnemies.push(type);
                    }
                }
            });
            
            if (weightedEnemies.length > 0) {
                const enemyType = weightedEnemies[Math.floor(Math.random() * weightedEnemies.length)];

                this.createEnemy(enemyType);
                this.lastEnemySpawn = now;
            }
        }
    }
    
    createEnemy(type) {
        const config = GameConfig.LEVEL_CONFIGS[this.level];
        const enemyConfig = config.enemies[type];
        
        let emoji;
        if (type === 'sharks') {
            emoji = GameConfig.ENEMIES.EMOJIS.sharks[Math.floor(Math.random() * GameConfig.ENEMIES.EMOJIS.sharks.length)];
        } else {
            emoji = GameConfig.ENEMIES.EMOJIS[type];
        }
        
        let size = GameConfig.ENEMIES.SIZES[type];
        
        const enemy = {
            type: type,
            emoji: emoji,
            size: size,
            speed: enemyConfig.speed,
            x: 0,
            y: 0,
            dx: 0,
            dy: 0
        };
        
        // Random spawn position (outside canvas)
        const side = Math.floor(Math.random() * 4);
        switch (side) {
            case 0: // Top
                enemy.x = Math.random() * this.canvas.width;
                enemy.y = -enemy.size;
                break;
            case 1: // Right
                enemy.x = this.canvas.width + enemy.size;
                enemy.y = Math.random() * this.canvas.height;
                break;
            case 2: // Bottom
                enemy.x = Math.random() * this.canvas.width;
                enemy.y = this.canvas.height + enemy.size;
                break;
            case 3: // Left
                enemy.x = -enemy.size;
                enemy.y = Math.random() * this.canvas.height;
                break;
        }
        
        // Initialize movement based on enemy type
        if (enemy.type === 'badgers') {
            // Badgers get random straight-line movement
            const angle = Math.random() * Math.PI * 2;
            enemy.dx = Math.cos(angle) * enemy.speed;
            enemy.dy = Math.sin(angle) * enemy.speed;
        } else if (enemy.type === 'sharks' || enemy.type === 'dinosaurs') {
            // Sharks and dinosaurs start with movement toward player
            const dx = this.movementManager.playerX - enemy.x;
            const dy = this.movementManager.playerY - enemy.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance > 0) {
                enemy.dx = (dx / distance) * enemy.speed;
                enemy.dy = (dy / distance) * enemy.speed;
            }
        }
        
        this.enemies.push(enemy);

    }
    
    updateEnemies() {
        for (let i = this.enemies.length - 1; i >= 0; i--) {
            const enemy = this.enemies[i];
            
            // Enemy AI based on type (consistent across all levels)
            if (enemy.type === 'badgers') {
                // Badgers: Always use pass-through behavior (award points when they exit screen)
                // They keep their initial direction and don't change course - no AI updates needed
            } else if (enemy.type === 'sharks') {
                // Sharks: Use "drift" behavior - they get initial direction toward player but don't constantly track
                // This makes them predictable and avoidable, less difficult than full tracking AI
                // No updates needed - they maintain their initial direction like badgers
            } else if (enemy.type === 'dinosaurs') {
                // Dinosaurs: Use full tracking AI (more dangerous than sharks)
                // Continuously update their movement to track the player
                const dx = this.movementManager.playerX - enemy.x;
                const dy = this.movementManager.playerY - enemy.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                if (distance > 0) {
                    enemy.dx = (dx / distance) * enemy.speed;
                    enemy.dy = (dy / distance) * enemy.speed;
                }
            }
            
            enemy.x += enemy.dx;
            enemy.y += enemy.dy;
            
            // Check if enemy has left the screen (use enemy size as margin to account for large enemies)
            const margin = Math.max(100, enemy.size);
            const leftScreen = enemy.x < -margin || enemy.x > this.canvas.width + margin ||
                             enemy.y < -margin || enemy.y > this.canvas.height + margin;
            
            if (leftScreen) {
                // Award points for badgers that pass through without collision
                if (enemy.type === 'badgers') {
                    this.addScore(10); // 10 points for avoiding a badger
                    console.log('🎯 Badger passed! +10 points');
                }
                this.enemies.splice(i, 1);
            }
        }
    }
    
    spawnPowerups(now) {
        // Handle mushroom spawning separately - maintain exactly 3 on screen
        this.maintainMushrooms();
        
        // Handle other powerups (microscope) with original logic
        const baseSpawnRate = GameConfig.POWERUPS.SPAWN_RATE;
        const levelSpeedMultiplier = Math.max(0.3, 1 - (this.level - 1) * 0.15);
        const scaledSpawnRate = baseSpawnRate * levelSpeedMultiplier;
        
        if (now - this.lastPowerupSpawn > scaledSpawnRate) {
            // Determine which powerup to spawn
            const rand = Math.random();
            
            // Check bomb spawning (level 3+, and only if inventory not full)
            if (this.level >= GameConfig.POWERUPS.BOMB.MIN_LEVEL && 
                this.bombCount < GameConfig.POWERUPS.BOMB.MAX_INVENTORY &&
                rand < GameConfig.POWERUPS.BOMB.SPAWN_CHANCE) {
                this.createBombPowerup();
            }
            // Check microscope spawning (level 2+)  
            else if (this.level >= GameConfig.POWERUPS.MICROSCOPE.MIN_LEVEL && 
                rand < GameConfig.POWERUPS.MICROSCOPE.SPAWN_CHANCE) {
                this.createMicroscopePowerup();
            }
            
            this.lastPowerupSpawn = now;
        }
    }
    
    maintainMushrooms() {
        const now = Date.now();
        
        // Count current mushrooms on screen
        const mushroomCount = this.powerups.filter(p => p.type === 'mushroom').length;
        const neededMushrooms = GameConfig.POWERUPS.MUSHROOM.MAX_ON_SCREEN - mushroomCount;
        
        // Only spawn one mushroom at a time with delay (always wait for interval)
        if (neededMushrooms > 0 && 
            now - this.lastMushroomSpawn >= GameConfig.POWERUPS.MUSHROOM.SPAWN_INTERVAL) {
            this.createMushroomPowerup();
            this.lastMushroomSpawn = now;
        }
    }
    
    createMushroomPowerup() {
        const powerup = {
            x: Math.random() * (this.canvas.width - GameConfig.POWERUPS.SIZE),
            y: Math.random() * (this.canvas.height - GameConfig.POWERUPS.SIZE),
            size: GameConfig.POWERUPS.SIZE,
            emoji: GameConfig.POWERUPS.MUSHROOM.EMOJI,
            type: 'mushroom',
            spawnTime: Date.now(),
            persistent: true // Mushrooms don't expire on their own
        };
        this.powerups.push(powerup);
    }
    
    createMicroscopePowerup() {
        const powerup = {
            x: Math.random() * (this.canvas.width - GameConfig.POWERUPS.SIZE),
            y: Math.random() * (this.canvas.height - GameConfig.POWERUPS.SIZE),
            size: GameConfig.POWERUPS.SIZE,
            emoji: GameConfig.POWERUPS.MICROSCOPE.EMOJI,
            type: 'microscope',
            spawnTime: Date.now(),
            persistent: false // Microscope powerups can expire
        };
        this.powerups.push(powerup);
    }
    
    createBombPowerup() {
        const powerup = {
            x: Math.random() * (this.canvas.width - GameConfig.POWERUPS.SIZE),
            y: Math.random() * (this.canvas.height - GameConfig.POWERUPS.SIZE),
            size: GameConfig.POWERUPS.SIZE,
            emoji: GameConfig.POWERUPS.BOMB.EMOJI,
            type: 'bomb',
            spawnTime: Date.now(),
            persistent: false // Bomb powerups can expire
        };
        this.powerups.push(powerup);
    }
    
    updatePowerups() {
        const now = Date.now();
        for (let i = this.powerups.length - 1; i >= 0; i--) {
            const powerup = this.powerups[i];
            
            // Update movement for bonus powerups
            if (powerup.type === 'bonus-trigger') {
                this.bonusManager.updateBonusTriggerMovement(powerup);
            } else if (powerup.type === 'bonus-boy') {
                this.bonusManager.updateBonusBoyMovement(powerup);
            }
            
            // Check lifetime based on powerup type - only for non-persistent powerups
            if (!powerup.persistent) {
                let lifetime;
                if (powerup.type === 'bonus-boy' || powerup.type === 'bonus-trigger') {
                    // Bonus powerups have their own lifetime property
                    lifetime = powerup.lifetime || GameConfig.BONUS.BOY_LIFETIME;
                } else {
                    // Regular powerups use global config
                    lifetime = GameConfig.POWERUPS.LIFETIME;
                }
                
                if (now - powerup.spawnTime > lifetime) {
                    // Notify BonusManager if a bonus boy expired
                    if (powerup.type === 'bonus-boy') {
                        this.bonusManager.collectBonusBoy(powerup);
                    }
                    
                    // Remove DOM element if it exists (for bonus powerups)
                    if (powerup.element && powerup.element.parentNode) {
                        powerup.element.parentNode.removeChild(powerup.element);
                    }
                    
                    this.powerups.splice(i, 1);
                    i--; // Adjust index after removal
                    continue;
                }
            }
        }
    }
    
    checkCollisions() {
        // Check enemy collisions (only if not in bonus round)
        if (!this.bonusManager.inBonusRound) {
            for (const enemy of this.enemies) {
                if (this.isColliding(
                    this.movementManager.playerX, this.movementManager.playerY, this.characterSize,
                    enemy.x, enemy.y, enemy.size
                )) {
                    this.endGame();
                    return;
                }
            }
        }
        
        // Check powerup collisions (always check)
        for (let i = this.powerups.length - 1; i >= 0; i--) {
            const powerup = this.powerups[i];
            if (this.isColliding(
                this.movementManager.playerX, this.movementManager.playerY, this.characterSize,
                powerup.x, powerup.y, powerup.size
            )) {
                this.collectPowerup(i);
            }
        }
    }
    
    isColliding(x1, y1, size1, x2, y2, size2) {
        const distance = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        return distance < (size1 + size2) / 2;
    }
    
    collectPowerup(index) {
        const powerup = this.powerups[index];
        this.powerups.splice(index, 1);
        
        // Check if this is a bonus powerup
        if (powerup.type === 'bonus-trigger') {
            // Trigger bonus round
            console.log('🎉 Bonus trigger collected! Starting bonus round...');
            this.showBonusRoundEffect(); // Show bonus round popup
            const bonusPoints = this.bonusManager.collectBonusTrigger(this.audioManager, this.powerups);
            this.bonusScore += bonusPoints;
            
            // Update instructions to show bonus round status
            const currentLevel = GameConfig.LEVEL_CONFIGS[this.level];
            this.updateInstructions(currentLevel.message);
        } else if (powerup.type === 'bonus-boy') {
            // Collect bonus boy powerup
            console.log('👦 Bonus boy collected! +50 points');
            const bonusPoints = this.bonusManager.collectBonusBoy(powerup);
            this.bonusScore += bonusPoints;
        } else if (powerup.type === 'microscope') {
            // Microscope powerup - reset player size to 100%
            console.log('🔬 Microscope collected! Player size reset to 100%');
            this.sizePercentage = GameConfig.POWERUPS.MICROSCOPE.SIZE_RESET;
            this.characterSize = (GameConfig.CHARACTER.BASE_SIZE * this.sizePercentage) / GameConfig.CHARACTER.BASE_PERCENTAGE;
            
            // Award points for collecting the microscope
            this.score += GameConfig.POWERUPS.MICROSCOPE.POINTS;
        } else if (powerup.type === 'bomb') {
            // Bomb powerup - add to inventory if not at max
            if (this.bombCount < GameConfig.POWERUPS.BOMB.MAX_INVENTORY) {
                this.bombCount++;
                this.score += GameConfig.POWERUPS.BOMB.POINTS;
                console.log(`💣 Bomb collected! Bombs: ${this.bombCount}/${GameConfig.POWERUPS.BOMB.MAX_INVENTORY}`);
                
                // Show popup
                this.showPointsPopup(powerup.x + powerup.size / 2, powerup.y, GameConfig.POWERUPS.BOMB.POINTS, 'Bomb acquired!');
            } else {
                console.log('💣 Bomb inventory full! Cannot collect more bombs.');
                // Still give some points even if inventory is full
                this.score += Math.floor(GameConfig.POWERUPS.BOMB.POINTS / 2);
                this.showPointsPopup(powerup.x + powerup.size / 2, powerup.y, Math.floor(GameConfig.POWERUPS.BOMB.POINTS / 2), 'Inventory full!');
            }
        } else if (powerup.type === 'mushroom') {
            // Mushroom powerup - implement combo system
            const now = Date.now();
            const timeSinceLastMushroom = now - this.lastMushroomTime;
            
            // Check if this is within combo window
            if (timeSinceLastMushroom <= GameConfig.POWERUPS.MUSHROOM.COMBO_WINDOW && this.lastMushroomTime > 0) {
                // Combo! Increase combo counter (cap at x5, which is mushroomCombo = 4)
                this.mushroomCombo = Math.min(this.mushroomCombo + 1, 4);
                
                // Calculate additional shrink and points
                const additionalShrink = GameConfig.POWERUPS.MUSHROOM.ADDITIONAL_SHRINK;
                const totalShrink = GameConfig.POWERUPS.MUSHROOM.BASE_SIZE_REDUCTION + additionalShrink;
                const comboMultiplier = Math.pow(GameConfig.POWERUPS.MUSHROOM.COMBO_MULTIPLIER, this.mushroomCombo);
                const points = Math.floor(GameConfig.POWERUPS.MUSHROOM.BASE_POINTS * comboMultiplier);
                
                // Apply shrinking
                this.sizePercentage = Math.max(GameConfig.CHARACTER.BASE_PERCENTAGE, this.sizePercentage - totalShrink);
                this.characterSize = (GameConfig.CHARACTER.BASE_SIZE * this.sizePercentage) / GameConfig.CHARACTER.BASE_PERCENTAGE;
                
                // Award combo points
                this.score += points;
                
                console.log(`🍄 Mushroom COMBO x${this.mushroomCombo + 1}! Extra shrink: ${additionalShrink}%, Points: ${points}`);
                
                // Show combo popup
                this.showPointsPopup(powerup.x + powerup.size / 2, powerup.y, points, `COMBO x${this.mushroomCombo + 1}!`);
            } else {
                // First mushroom or combo expired - reset combo
                this.mushroomCombo = 0;
                const points = GameConfig.POWERUPS.MUSHROOM.BASE_POINTS;
                
                // Apply base shrinking
                this.sizePercentage = Math.max(GameConfig.CHARACTER.BASE_PERCENTAGE, this.sizePercentage - GameConfig.POWERUPS.MUSHROOM.BASE_SIZE_REDUCTION);
                this.characterSize = (GameConfig.CHARACTER.BASE_SIZE * this.sizePercentage) / GameConfig.CHARACTER.BASE_PERCENTAGE;
                
                // Award base points
                this.score += points;
                
                console.log(`🍄 Mushroom collected! Size: ${this.sizePercentage}%, Points: ${points}`);
                
                // Show regular popup
                this.showPointsPopup(powerup.x + powerup.size / 2, powerup.y, points);
            }
            
            // Update last mushroom time
            this.lastMushroomTime = now;
        } else {
            // Fallback for any other powerup types
            console.log('Unknown powerup collected!');
            this.score += 10;
        }
        
        this.audioManager.playPowerupSound();
        this.updateUI(); // This will update the combo display immediately
    }
    
    showPointsPopup(x, y, points, comboText = null) {
        const popup = {
            x: x,
            y: y,
            points: points,
            comboText: comboText,
            startTime: Date.now(),
            duration: 2000, // 2 seconds
            color: comboText ? '#ffff00' : '#00ff00' // Yellow for combo, green for regular
        };
        this.popupTexts.push(popup);
    }
    
    updatePopupTexts() {
        const now = Date.now();
        this.popupTexts = this.popupTexts.filter(popup => {
            return (now - popup.startTime) < popup.duration;
        });
    }
    
    renderExplosions() {
        if (!this.explosions) return;
        
        for (const explosion of this.explosions) {
            // Use the radius and alpha calculated in updateExplosions
            this.ctx.save();
            this.ctx.globalAlpha = explosion.alpha;
            this.ctx.strokeStyle = '#FF6B6B';
            this.ctx.lineWidth = 3;
            this.ctx.beginPath();
            this.ctx.arc(explosion.x, explosion.y, explosion.radius, 0, Math.PI * 2);
            this.ctx.stroke();
            
            // Add inner circle for more effect
            this.ctx.globalAlpha = explosion.alpha * 0.3;
            this.ctx.fillStyle = '#FF6B6B';
            this.ctx.fill();
            
            this.ctx.restore();
        }
    }
    
    updateExplosions() {
        if (!this.explosions) return;
        
        const now = Date.now();
        
        // Update each explosion and handle enemy destruction
        for (const explosion of this.explosions) {
            const elapsed = now - explosion.startTime;
            const progress = Math.min(elapsed / explosion.duration, 1);
            
            // Calculate current radius
            const currentRadius = explosion.maxRadius * progress;
            
            // Check for enemies that should be destroyed at this radius
            const originalEnemyCount = this.enemies.length;
            this.enemies = this.enemies.filter(enemy => {
                const dx = enemy.x - explosion.x;
                const dy = enemy.y - explosion.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                // If enemy is within current explosion radius, destroy it
                if (distance <= currentRadius) {
                    explosion.enemiesDestroyed++;
                    explosion.totalBonusPoints += 5; // 5 points per enemy
                    return false; // Remove enemy
                }
                return true; // Keep enemy
            });
            
            // Update explosion radius for rendering
            explosion.radius = currentRadius;
            explosion.alpha = 1 - progress;
        }
        
        // Remove completed explosions and show final score popup
        this.explosions = this.explosions.filter(explosion => {
            const isComplete = (now - explosion.startTime) >= explosion.duration;
            
            if (isComplete && explosion.enemiesDestroyed > 0) {
                // Add points and show popup when explosion finishes
                this.score += explosion.totalBonusPoints;
                this.showPointsPopup(
                    explosion.x, 
                    explosion.y, 
                    explosion.totalBonusPoints, 
                    `💥 ${explosion.enemiesDestroyed} enemies destroyed!`
                );
                console.log(`💥 Explosion complete! Destroyed ${explosion.enemiesDestroyed} enemies for ${explosion.totalBonusPoints} points.`);
            }
            
            return !isComplete;
        });
    }
    
    checkLevelProgression() {
        // Don't advance levels during bonus rounds
        if (this.bonusManager.inBonusRound) {
            return;
        }
        
        const nextLevel = this.level + 1;
        const nextLevelConfig = GameConfig.LEVEL_CONFIGS[nextLevel];
        
        if (nextLevelConfig) {
            // Calculate required points as current score + level points (additive system)
            const currentLevelConfig = GameConfig.LEVEL_CONFIGS[this.level];
            const currentLevelThreshold = currentLevelConfig ? currentLevelConfig.pointsRequired : 0;
            const requiredScore = currentLevelThreshold + nextLevelConfig.pointsRequired;
            
            if (this.score >= requiredScore) {
                this.advanceToLevel(nextLevel);
            }
        }
    }
    
    checkComboExpiration(now) {
        // Check if combo should expire (only if we have an active combo and a last mushroom time)
        if (this.mushroomCombo > 0 && this.lastMushroomTime > 0) {
            const timeSinceLastMushroom = now - this.lastMushroomTime;
            if (timeSinceLastMushroom > GameConfig.POWERUPS.MUSHROOM.COMBO_WINDOW) {
                // Combo has expired, reset it
                this.mushroomCombo = 0;
                this.updateUI(); // Update the header immediately
            }
        }
    }
    
    advanceToLevel(newLevel) {
        this.level = newLevel;
        const config = GameConfig.LEVEL_CONFIGS[newLevel];
        
        console.log(`🎯 LEVEL UP! Advanced to Level ${newLevel}`);
        
        // Show visual level up feedback
        this.showLevelUpEffect(newLevel);
        
        // Update movement manager with new speed limits
        this.movementManager.updateSpeedLimits(config.playerSpeed.max, config.playerSpeed.preGame);
        
        this.updateInstructions(config.message);
        this.updateUI();
        
        // Start bonus round
        this.bonusManager.startBonusRound(this.gameRunning, () => {
            this.bonusManager.spawnBonusTrigger(this.powerups, this.audioManager);
        });
    }
    
    showLevelUpEffect(level) {
        // Create level up notification (smaller and positioned at top)
        const levelUpDiv = document.createElement('div');
        levelUpDiv.id = 'level-up-effect';
        levelUpDiv.innerHTML = `
            <div class="level-up-content">
                <div class="level-up-main">🎉 LEVEL ${level}! 🎉</div>
                <div class="level-up-sub">${GameConfig.LEVEL_CONFIGS[level].message}</div>
            </div>
        `;
        
        // Add styles - smaller notification at top center
        levelUpDiv.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.9);
            border: 2px solid #4CAF50;
            border-radius: 15px;
            padding: 20px 30px;
            z-index: 3000;
            animation: levelUpSlideDown 0.5s ease-out;
            box-shadow: 0 5px 20px rgba(0,0,0,0.8);
        `;
        
        const style = document.createElement('style');
        style.textContent = `
            @keyframes levelUpSlideDown {
                from { 
                    opacity: 0; 
                    transform: translateX(-50%) translateY(-50px);
                }
                to { 
                    opacity: 1; 
                    transform: translateX(-50%) translateY(0);
                }
            }
            
            @keyframes levelUpGlow {
                0%, 100% { text-shadow: 0 0 5px #4CAF50; }
                50% { text-shadow: 0 0 20px #4CAF50, 0 0 30px #4CAF50; }
            }
            
            .level-up-content {
                text-align: center;
                color: white;
                font-family: Arial, sans-serif;
            }
            
            .level-up-main {
                font-size: 1.8em;
                font-weight: bold;
                margin-bottom: 8px;
                color: #4CAF50;
                animation: levelUpGlow 2s ease-in-out infinite;
            }
            
            .level-up-sub {
                font-size: 1em;
                color: #ffeb3b;
                text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
            }
        `;
        
        document.head.appendChild(style);
        document.body.appendChild(levelUpDiv);
        
        // Play level up sound effect (reuse powerup sound with higher pitch)
        this.audioManager.playPowerupSound();
        
        // Remove after 2.5 seconds (shorter duration)
        setTimeout(() => {
            if (levelUpDiv.parentNode) {
                levelUpDiv.remove();
                style.remove();
            }
        }, 2500);
    }
    
    showBonusRoundEffect() {
        // Create bonus round notification
        const bonusDiv = document.createElement('div');
        bonusDiv.id = 'bonus-round-effect';
        bonusDiv.innerHTML = `
            <div class="bonus-round-content">
                <div class="bonus-round-main">🎉 BONUS ROUND! 🎉</div>
                <div class="bonus-round-sub">Collect the moving boys for extra points!</div>
            </div>
        `;
        
        // Add styles - same position as level up notification
        bonusDiv.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(255, 107, 53, 0.9);
            border: 2px solid #ffeb3b;
            border-radius: 15px;
            padding: 20px 30px;
            z-index: 3000;
            animation: bonusRoundSlideDown 0.5s ease-out;
            box-shadow: 0 5px 20px rgba(255, 107, 53, 0.8);
        `;
        
        const style = document.createElement('style');
        style.textContent = `
            @keyframes bonusRoundSlideDown {
                from { 
                    opacity: 0; 
                    transform: translateX(-50%) translateY(-50px);
                }
                to { 
                    opacity: 1; 
                    transform: translateX(-50%) translateY(0);
                }
            }
            
            @keyframes bonusRoundGlow {
                0%, 100% { text-shadow: 0 0 10px #ffeb3b; }
                50% { text-shadow: 0 0 20px #ffeb3b, 0 0 40px #ff6b35; }
            }
            
            .bonus-round-content {
                text-align: center;
                color: white;
                font-family: Arial, sans-serif;
            }
            
            .bonus-round-main {
                font-size: 2.2em;
                font-weight: bold;
                margin-bottom: 10px;
                color: #ffeb3b;
                animation: bonusRoundGlow 1.5s ease-in-out infinite;
                text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
            }
            
            .bonus-round-sub {
                font-size: 1.2em;
                color: white;
                text-shadow: 1px 1px 3px rgba(0,0,0,0.8);
            }
        `;
        
        document.head.appendChild(style);
        document.body.appendChild(bonusDiv);
        
        // Play bonus sound effect
        this.audioManager.playPowerupSound();
        
        // Remove after 3 seconds
        setTimeout(() => {
            if (bonusDiv.parentNode) {
                bonusDiv.remove();
                style.remove();
            }
        }, 3000);
    }
    
    showScoreSubmissionNotification(success, playerName) {
        // Create submission notification
        const notificationDiv = document.createElement('div');
        notificationDiv.id = 'score-notification';
        
        if (success) {
            notificationDiv.innerHTML = `
                <div class="notification-content">
                    <div class="notification-icon">🎉</div>
                    <div class="notification-text">
                        <div class="notification-main">Score Submitted!</div>
                        <div class="notification-sub">Welcome to the leaderboard, ${playerName}!</div>
                    </div>
                </div>
            `;
        } else {
            notificationDiv.innerHTML = `
                <div class="notification-content">
                    <div class="notification-icon">❌</div>
                    <div class="notification-text">
                        <div class="notification-main">Submission Failed</div>
                        <div class="notification-sub">Please try again</div>
                    </div>
                </div>
            `;
        }
        
        // Position at top-right corner
        notificationDiv.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${success ? 'rgba(76, 175, 80, 0.95)' : 'rgba(244, 67, 54, 0.95)'};
            border: 2px solid ${success ? '#4CAF50' : '#f44336'};
            border-radius: 10px;
            padding: 15px 20px;
            z-index: 3500;
            color: white;
            font-family: Arial, sans-serif;
            box-shadow: 0 4px 15px rgba(0,0,0,0.3);
            animation: slideInRight 0.4s ease-out;
            min-width: 250px;
        `;
        
        const style = document.createElement('style');
        style.id = 'notification-styles';
        style.textContent = `
            @keyframes slideInRight {
                from {
                    opacity: 0;
                    transform: translateX(100%);
                }
                to {
                    opacity: 1;
                    transform: translateX(0);
                }
            }
            
            @keyframes slideOutRight {
                from {
                    opacity: 1;
                    transform: translateX(0);
                }
                to {
                    opacity: 0;
                    transform: translateX(100%);
                }
            }
            
            .notification-content {
                display: flex;
                align-items: center;
                gap: 12px;
            }
            
            .notification-icon {
                font-size: 2em;
                flex-shrink: 0;
            }
            
            .notification-text {
                flex: 1;
            }
            
            .notification-main {
                font-size: 1.1em;
                font-weight: bold;
                margin-bottom: 2px;
            }
            
            .notification-sub {
                font-size: 0.9em;
                opacity: 0.9;
            }
        `;
        
        document.head.appendChild(style);
        document.body.appendChild(notificationDiv);
        
        // Remove after 4 seconds with slide-out animation
        setTimeout(() => {
            notificationDiv.style.animation = 'slideOutRight 0.4s ease-in';
            setTimeout(() => {
                if (notificationDiv.parentNode) {
                    notificationDiv.remove();
                }
                if (style.parentNode) {
                    style.remove();
                }
            }, 400);
        }, 4000);
    }
    
    addScore(points) {
        this.score += points;
        this.updateUI();
    }
    
    endGame() {
        this.gameRunning = false;
        this.gameOver = true;
        
        this.audioManager.stopAllAudio();
        this.audioManager.playGameOverSound();
        
        // Update high score display (but don't save yet - wait for user to submit)
        if (this.score > this.highScore) {
            this.highScore = this.score;
        }
        
        this.updateUI();
        this.updateInstructions("Game Over!");
        
        // Show game over modal
        this.showGameOverModal();
    }
    
    showGameOverModal() {
        // Update final score display (show total score including bonus)
        const finalScoreElement = document.getElementById('final-score');
        const totalScore = this.score + this.bonusScore;
        if (finalScoreElement) {
            finalScoreElement.textContent = `Your Score: ${totalScore}`;
        }
        
        // Load remembered player name
        const playerNameInput = document.getElementById('player-name');
        if (playerNameInput) {
            const rememberedName = localStorage.getItem('saboteurPlayerName');
            if (rememberedName) {
                playerNameInput.value = rememberedName;
            }
        }
        
        // Show the modal
        const modal = document.getElementById('highscore-modal');
        if (modal) {
            modal.classList.remove('hidden');
        }
        
        // Load and display high scores table
        this.loadAndDisplayHighScores();
        
        // Update submit button state based on whether level was skipped
        const submitBtn = document.getElementById('submit-score');
        if (submitBtn) {
            if (this.levelSkipped) {
                submitBtn.textContent = 'Level Skipped';
                submitBtn.style.backgroundColor = '#dc3545';
                submitBtn.style.opacity = '0.6';
                submitBtn.disabled = true;
            } else {
                // Reset to normal state for legitimate games
                submitBtn.textContent = 'Submit Score';
                submitBtn.style.backgroundColor = '#4CAF50';
                submitBtn.style.opacity = '1';
                submitBtn.disabled = false;
            }
        }
        
        // Set up event listeners for modal buttons
        this.setupModalEventListeners();
    }
    
    setupModalEventListeners() {
        // Play Again button
        const playAgainBtn = document.getElementById('play-again');
        if (playAgainBtn) {
            playAgainBtn.onclick = () => {
                this.hideGameOverModal();
                this.resetGame();
            };
        }
        
        // Get references for input and button
        const playerNameInput = document.getElementById('player-name');
        const submitBtn = document.getElementById('submit-score');
        
        // Create a shared submit function
        const submitScore = async () => {
            if (!submitBtn) return;
            
            // Check if button is already disabled (submitting)
            if (submitBtn.disabled && submitBtn.textContent.includes('Submitting')) {
                return;
            }
            
            // Check if level was skipped - disable score submission if so
            if (this.levelSkipped) {
                submitBtn.textContent = '❌ Skipped Level - No Submit';
                submitBtn.style.backgroundColor = '#dc3545';
                submitBtn.disabled = true;
                setTimeout(() => {
                    submitBtn.textContent = 'Level Skipped';
                    submitBtn.style.opacity = '0.6';
                }, 2000);
                return;
            }
            
            const playerName = playerNameInput ? playerNameInput.value.trim() || 'Player' : 'Player';
            
            // Show submitting feedback
            submitBtn.disabled = true;
            submitBtn.textContent = '⏳ Submitting...';
            submitBtn.style.backgroundColor = '#ff9800';
            submitBtn.style.opacity = '0.8';
            
            // Save player name to localStorage for future games
            if (playerNameInput && playerNameInput.value.trim()) {
                localStorage.setItem('saboteurPlayerName', playerNameInput.value.trim());
            }
            
            try {
                // Submit score to server
                const submitSuccess = await this.submitScore(playerName);
                
                if (submitSuccess !== false) {
                    // Show success feedback
                    submitBtn.textContent = '✅ Success!';
                    submitBtn.style.backgroundColor = '#4CAF50';
                    submitBtn.style.opacity = '1';
                    submitBtn.disabled = true; // Keep disabled after successful submission
                    
                    // Reload the high scores table to show the updated leaderboard
                    await this.loadAndDisplayHighScores();
                    
                    // Show success notification and final state
                    this.showScoreSubmissionNotification(true, playerName);
                    setTimeout(() => {
                        submitBtn.textContent = '🏆 Score Submitted!';
                        submitBtn.style.opacity = '0.6';
                    }, 1000);
                    
                } else {
                    // Show error message for invalid submission - re-enable button
                    submitBtn.textContent = '❌ Invalid Score!';
                    submitBtn.style.backgroundColor = '#dc3545';
                    submitBtn.disabled = false;
                    setTimeout(() => {
                        submitBtn.textContent = 'Submit Score';
                        submitBtn.style.backgroundColor = '#4CAF50';
                        submitBtn.style.opacity = '1';
                        submitBtn.disabled = false; // Ensure button is clickable again
                    }, 2500);
                }
            } catch (error) {
                // Handle network/server errors - re-enable button
                submitBtn.textContent = '🌐 Connection Error';
                submitBtn.style.backgroundColor = '#dc3545';
                submitBtn.disabled = false;
                setTimeout(() => {
                    submitBtn.textContent = 'Submit Score';
                    submitBtn.style.backgroundColor = '#4CAF50';
                    submitBtn.style.opacity = '1';
                    submitBtn.disabled = false; // Ensure button is clickable again
                }, 3000);
            }
        };
        
        // Submit Score button click handler
        if (submitBtn) {
            submitBtn.onclick = submitScore;
        }
        
        // Add Enter key support to player name input
        if (playerNameInput) {
            playerNameInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    submitScore();
                }
            });
        }
    }
    
    hideGameOverModal() {
        const modal = document.getElementById('highscore-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    }
    
    resetGame() {
        // Reset all game state
        this.gameStarted = false;
        this.gameRunning = false;
        this.gameOver = false;
        this.gamePaused = false;
        this.showBonusTimer = false;
        
        this.score = 0;
        this.bonusScore = 0; // Reset bonus score
        this.level = 1;
        this.sizePercentage = GameConfig.CHARACTER.BASE_PERCENTAGE;
        this.characterSize = GameConfig.CHARACTER.BASE_SIZE;
        this.levelSkipped = false; // Reset level skip flag
        this.bombCount = 0; // Reset bomb inventory
        
        // Reset mushroom combo system
        this.lastMushroomTime = 0;
        this.mushroomCombo = 0;
        this.popupTexts = [];
        
        this.startTime = null;
        this.gameStartTime = null;
        this.bonusTimerEnd = null;
        this.lastGrowthTime = 0;
        this.bonusCountdownStart = null;
        
        this.enemies = [];
        this.powerups = [];
        this.lastPowerupSpawn = 0;
        this.lastEnemySpawn = 0;
        this.lastMushroomSpawn = 0; // Reset mushroom spawn timing
        
        // Reset managers
        this.movementManager.reset();
        this.bonusManager.reset();
        
        // Reset player position to center
        this.movementManager.updatePlayerPosition();
        
        // Reset UI
        this.updateUI();
        this.updateInstructions("Click or press Space to start the game!");
        
        // Start pre-game loop to show player movement
        this.preGameLoop();
    }
    
    restartGame() {
        // Hide any open modals
        this.hideGameOverModal();
        
        // Stop background music properly
        this.audioManager.stopBackgroundMusic();
        
        // Reset game state
        this.resetGame();
        
        console.log('🔄 Game restarted with R key');
    }
    
    togglePause() {
        if (!this.gameStarted || this.gameOver) {
            return; // Can't pause if game hasn't started or is over
        }
        
        this.gamePaused = !this.gamePaused;
        
        if (this.gamePaused) {
            // Pause background music
            this.audioManager.pauseBackgroundMusic();
            this.showPauseOverlay();
            console.log('⏸️ Game paused');
        } else {
            // Resume background music
            this.audioManager.resumeBackgroundMusic();
            this.hidePauseOverlay();
            console.log('▶️ Game resumed');
        }
    }
    
    showPauseOverlay() {
        // Create pause overlay
        const pauseDiv = document.createElement('div');
        pauseDiv.id = 'pause-overlay';
        pauseDiv.innerHTML = `
            <div class="pause-content">
                <div class="pause-main">⏸️ PAUSED</div>
                <div class="pause-sub">Press P to resume</div>
            </div>
        `;
        
        pauseDiv.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2500;
            font-family: Arial, sans-serif;
        `;
        
        const style = document.createElement('style');
        style.id = 'pause-styles';
        style.textContent = `
            .pause-content {
                text-align: center;
                color: white;
            }
            
            .pause-main {
                font-size: 3em;
                font-weight: bold;
                text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
                margin-bottom: 20px;
            }
            
            .pause-sub {
                font-size: 1.2em;
                text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
                color: #ffeb3b;
            }
        `;
        
        document.head.appendChild(style);
        document.body.appendChild(pauseDiv);
    }
    
    hidePauseOverlay() {
        const pauseDiv = document.getElementById('pause-overlay');
        const pauseStyle = document.getElementById('pause-styles');
        
        if (pauseDiv) pauseDiv.remove();
        if (pauseStyle) pauseStyle.remove();
    }
    
    render() {
        // Clear canvas
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Render bonus round elements
        this.bonusManager.render(this.ctx);
        
        // Render player (always visible unless game over)
        if (!this.gameOver) {
            this.renderPlayer();
        }
        
        // Render enemies (only if not in bonus round)
        if (!this.bonusManager.inBonusRound) {
            this.renderEnemies();
        }
        
        // Always render powerups (includes bonus powerups during bonus round)
        this.renderPowerups();
        
        // Render popup texts
        this.renderPopupTexts();
        
        // Render explosions
        this.renderExplosions();
        
        // Update popup texts (remove expired ones)
        this.updatePopupTexts();
        
        // Update explosions (remove expired ones)
        this.updateExplosions();
        
        // Render bonus timer
        if (this.showBonusTimer && this.bonusTimerEnd) {
            this.renderBonusTimer();
        }
    }
    
    renderPlayer() {
        // Only render the player if the character image has loaded
        if (this.characterImageLoaded) {
            // Maintain aspect ratio based on the image's natural dimensions
            const imageAspectRatio = this.characterImage.naturalWidth / this.characterImage.naturalHeight;
            
            let drawWidth, drawHeight;
            if (imageAspectRatio > 1) {
                // Image is wider than it is tall
                drawWidth = this.characterSize;
                drawHeight = this.characterSize / imageAspectRatio;
            } else {
                // Image is taller than it is wide (or square)
                drawWidth = this.characterSize * imageAspectRatio;
                drawHeight = this.characterSize;
            }
            
            // Calculate position to center the image
            const x = this.movementManager.playerX - drawWidth / 2;
            const y = this.movementManager.playerY - drawHeight / 2;
            
            // Draw the character image with correct aspect ratio
            this.ctx.drawImage(this.characterImage, x, y, drawWidth, drawHeight);
        }
        // No fallback - simply don't render anything until image loads
    }
    
    renderEnemies() {

        
        for (const enemy of this.enemies) {
            this.ctx.font = `${enemy.size}px Arial`;
            this.ctx.textAlign = 'center';
            this.ctx.fillText(enemy.emoji, enemy.x, enemy.y + enemy.size / 3);
        }
    }
    
    renderPowerups() {
        const now = Date.now();
        
        for (const powerup of this.powerups) {
            this.ctx.textAlign = 'center';
            
            // Special rendering for bonus powerups
            if (powerup.type === 'bonus-trigger') {
                // Pulsing effect for bonus trigger
                const pulseScale = 1 + 0.2 * Math.sin((now - powerup.spawnTime) * 0.01);
                const size = powerup.size * 2.5 * pulseScale; // Make it larger and pulsing
                
                // Add glow effect
                this.ctx.shadowColor = '#ffff00';
                this.ctx.shadowBlur = 20;
                
                this.ctx.font = `${size}px Arial`;
                this.ctx.fillText(powerup.emoji, powerup.x + powerup.size / 2, powerup.y + powerup.size);
                
                // Reset shadow
                this.ctx.shadowColor = 'transparent';
                this.ctx.shadowBlur = 0;
                
            } else if (powerup.type === 'bonus-boy') {
                // Bouncing effect for bonus boys
                const bounceOffset = 10 * Math.sin((now - powerup.spawnTime) * 0.005 + (powerup.animationOffset || 0));
                const size = powerup.size * 1.8; // Larger than regular powerups
                
                this.ctx.font = `${size}px Arial`;
                this.ctx.fillText(powerup.emoji, powerup.x + powerup.size / 2, powerup.y + powerup.size + bounceOffset);
                
            } else if (powerup.type === 'microscope') {
                // Special rendering for microscope powerup - subtle glow to indicate rarity
                this.ctx.shadowColor = '#00ccff';
                this.ctx.shadowBlur = 10;
                
                const size = powerup.size * 1.3; // Slightly larger than regular powerups
                this.ctx.font = `${size}px Arial`;
                this.ctx.fillText(powerup.emoji, powerup.x + powerup.size / 2, powerup.y + powerup.size);
                
                // Reset shadow
                this.ctx.shadowColor = 'transparent';
                this.ctx.shadowBlur = 0;
                
            } else if (powerup.type === 'mushroom') {
                // Special rendering for mushroom powerup - apply blue filter effect
                // Save current state
                this.ctx.save();
                
                // Apply blue filter using hue rotation and saturation
                this.ctx.filter = 'hue-rotate(240deg) saturate(1.5) brightness(0.9)';
                
                this.ctx.font = `${powerup.size}px Arial`;
                this.ctx.fillText(powerup.emoji, powerup.x + powerup.size / 2, powerup.y + powerup.size);
                
                // Reset filter and restore state
                this.ctx.filter = 'none';
                this.ctx.restore();
                
            } else if (!this.bonusManager.inBonusRound) {
                // Regular powerup rendering (only when not in bonus round)
                this.ctx.font = `${powerup.size}px Arial`;
                this.ctx.fillText(powerup.emoji, powerup.x + powerup.size / 2, powerup.y + powerup.size);
            }
        }
    }
    
    renderBonusTimer() {
        const timeLeft = Math.max(0, (this.bonusTimerEnd - Date.now()) / 1000);
        this.ctx.font = '48px Arial';
        this.ctx.fillStyle = timeLeft <= 1 ? 'red' : 'orange';
        this.ctx.textAlign = 'center';
        this.ctx.fillText(
            `${Math.ceil(timeLeft)}`,
            this.canvas.width / 2,
            this.canvas.height / 2 - 50
        );
    }
    
    renderPopupTexts() {
        const now = Date.now();
        
        for (const popup of this.popupTexts) {
            const elapsed = now - popup.startTime;
            const progress = elapsed / popup.duration;
            
            // Calculate animation values
            const opacity = Math.max(0, 1 - progress);
            const yOffset = progress * 60; // Float upward
            const scale = 1 + progress * 0.5; // Grow slightly
            
            // Set text properties
            this.ctx.save();
            this.ctx.globalAlpha = opacity;
            this.ctx.textAlign = 'center';
            this.ctx.fillStyle = popup.color;
            this.ctx.strokeStyle = '#000000';
            this.ctx.lineWidth = 2;
            
            // Draw combo text if present
            if (popup.comboText) {
                this.ctx.font = `bold ${Math.floor(16 * scale)}px Arial`;
                const comboY = popup.y - yOffset - 20;
                this.ctx.strokeText(popup.comboText, popup.x, comboY);
                this.ctx.fillText(popup.comboText, popup.x, comboY);
            }
            
            // Draw points
            this.ctx.font = `bold ${Math.floor(20 * scale)}px Arial`;
            const pointsY = popup.y - yOffset;
            const pointsText = `+${popup.points}`;
            this.ctx.strokeText(pointsText, popup.x, pointsY);
            this.ctx.fillText(pointsText, popup.x, pointsY);
            
            this.ctx.restore();
        }
    }
    
    updateUI() {
        // Display total score (regular + bonus points)
        const totalScore = this.score + this.bonusScore;
        const scoreElement = document.getElementById('score');
        const highScoreElement = document.getElementById('high-score');
        const levelElement = document.getElementById('level');
        const sizeElement = document.getElementById('size');
        const comboElement = document.getElementById('combo');
        const bombCountElement = document.getElementById('bomb-count');
        
        if (scoreElement) scoreElement.textContent = totalScore;
        if (highScoreElement) highScoreElement.textContent = this.highScore;
        if (levelElement) levelElement.textContent = this.level;
        if (sizeElement) sizeElement.textContent = `${this.sizePercentage}%`;
        if (bombCountElement) bombCountElement.textContent = this.bombCount;
        
        // Display current combo multiplier
        if (comboElement) {
            const comboMultiplier = this.mushroomCombo + 1; // +1 because combo starts at 0
            comboElement.textContent = `x${comboMultiplier}`;
        }
        
        // Calculate points needed for next level (additive system)
        const nextLevel = this.level + 1;
        const nextLevelConfig = GameConfig.LEVEL_CONFIGS[nextLevel];
        const nextLevelElement = document.getElementById('next-level');
        
        if (nextLevelConfig && nextLevelElement) {
            // Calculate required points as current level threshold + next level points
            const currentLevelConfig = GameConfig.LEVEL_CONFIGS[this.level];
            const currentLevelThreshold = currentLevelConfig ? currentLevelConfig.pointsRequired : 0;
            const requiredScore = currentLevelThreshold + nextLevelConfig.pointsRequired;
            const pointsNeeded = Math.max(0, requiredScore - this.score);
            nextLevelElement.textContent = pointsNeeded;
        } else if (nextLevelElement) {
            // Max level reached
            nextLevelElement.textContent = "MAX";
        }
    }
    
    updateInstructions(message) {
        let displayMessage = message;
        
        // Replace with bonus round message if in bonus round
        if (this.bonusManager.inBonusRound) {
            displayMessage = "🎉 BONUS ROUND - Collect the boys!";
        }
        
        document.getElementById('instructions').textContent = displayMessage;
    }
    
    async loadHighScore() {
        try {
            const response = await fetch('https://api.jsonbin.io/v3/b/68f387e6d0ea881f40a9d9df/latest', {
                method: 'GET',
                headers: {
                    'X-Master-Key': '$2a$10$SsNUmxJ8chY5iwcOmp9fXezF3/5EQf32igtdAKkTbJ5gBIClY1c4q'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                // Extract high score from the record structure
                const record = data.record || [];
                this.highScore = Array.isArray(record) && record.length > 0 ? 
                    Math.max(...record.map(score => score.score || 0)) : 0;
                this.updateUI();
            } else {
                this.highScore = 0;
                this.updateUI();
            }
        } catch (error) {
            console.log('Could not load high score from server');
            this.highScore = 0;
            this.updateUI();
        }
    }
    
    async saveHighScore() {
        try {
            // First, load current high scores to preserve them
            const loadResponse = await fetch('https://api.jsonbin.io/v3/b/68f387e6d0ea881f40a9d9df/latest', {
                method: 'GET',
                headers: {
                    'X-Master-Key': '$2a$10$SsNUmxJ8chY5iwcOmp9fXezF3/5EQf32igtdAKkTbJ5gBIClY1c4q'
                }
            });
            
            let existingScores = [];
            if (loadResponse.ok) {
                const loadData = await loadResponse.json();
                existingScores = Array.isArray(loadData.record) ? loadData.record : [];
            }
            
            // Add new high score if it's actually higher than existing ones
            const currentHighScore = existingScores.length > 0 ? 
                Math.max(...existingScores.map(score => score.score || 0)) : 0;
            
            if (this.score > currentHighScore) {
                const newScore = {
                    name: 'Player',
                    score: this.score,
                    level: this.level,
                    date: this.formatUKDateTime()
                };
                existingScores.push(newScore);
                
                // Save updated scores
                const response = await fetch('https://api.jsonbin.io/v3/b/68f387e6d0ea881f40a9d9df', {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Master-Key': '$2a$10$SsNUmxJ8chY5iwcOmp9fXezF3/5EQf32igtdAKkTbJ5gBIClY1c4q'
                    },
                    body: JSON.stringify(existingScores)
                });
                
                if (!response.ok) {
                    throw new Error('Failed to save to server');
                }
            }
        } catch (error) {
            console.log('Could not save high score to server');
        }
    }

    async submitScore(playerName) {
        // Calculate total score and validate before submitting
        const totalScore = this.score + this.bonusScore;
        if (totalScore <= 0) {
            console.log('Score validation failed: Score must be greater than 0');
            return false;
        }
        
        if (!playerName || playerName.trim() === '' || playerName.trim() === 'Player') {
            console.log('Score validation failed: Invalid player name');
            return false;
        }
        
        try {
            // Get existing scores
            const response = await fetch('https://api.jsonbin.io/v3/b/68f387e6d0ea881f40a9d9df/latest', {
                method: 'GET',
                headers: {
                    'X-Master-Key': '$2a$10$SsNUmxJ8chY5iwcOmp9fXezF3/5EQf32igtdAKkTbJ5gBIClY1c4q'
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch scores');
            }
            
            const data = await response.json();
            const existingScores = Array.isArray(data.record) ? data.record : [];
            
            // Add new score (include total score with bonus points)
            const newScore = {
                name: playerName,
                score: totalScore,
                level: this.level,
                date: this.formatUKDateTime()
            };
            existingScores.push(newScore);
            
            // Sort by score (highest first) and keep top 10
            existingScores.sort((a, b) => (b.score || 0) - (a.score || 0));
            const topScores = existingScores.slice(0, 10);
            
            // Save updated scores
            const saveResponse = await fetch('https://api.jsonbin.io/v3/b/68f387e6d0ea881f40a9d9df', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Master-Key': '$2a$10$SsNUmxJ8chY5iwcOmp9fXezF3/5EQf32igtdAKkTbJ5gBIClY1c4q'
                },
                body: JSON.stringify(topScores)
            });
            
            if (!saveResponse.ok) {
                throw new Error('Failed to save score to server');
            }
            
            console.log(`Score submitted successfully: ${playerName} - ${this.score}`);
            return true;
            
        } catch (error) {
            console.log('Could not submit score to server:', error);
            return false;
        }
    }

    async loadAndDisplayHighScores() {
        // Show loading state and hide table initially
        const loadingDiv = document.getElementById('highscore-loading');
        const table = document.querySelector('#highscore-table table');
        
        if (loadingDiv) loadingDiv.style.display = 'block';
        if (table) table.style.display = 'none';
        
        try {
            const response = await fetch('https://api.jsonbin.io/v3/b/68f387e6d0ea881f40a9d9df/latest', {
                method: 'GET',
                headers: {
                    'X-Master-Key': '$2a$10$SsNUmxJ8chY5iwcOmp9fXezF3/5EQf32igtdAKkTbJ5gBIClY1c4q'
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch scores');
            }
            
            const data = await response.json();
            const scores = Array.isArray(data.record) ? data.record : [];
            
            // Filter out blank/invalid records and sort by score value (highest first)
            const validScores = scores.filter(score => {
                // Filter out records that are clearly blank or invalid
                return score.score > 0 && // Must have a positive score
                       score.name && // Must have a name
                       score.name.trim() !== '' && // Name must not be empty
                       score.name !== 'Player' && // Filter out default "Player" name
                       score.date && // Must have a date
                       score.date !== 'Unknown'; // Date must not be "Unknown"
            });
            
            const topScores = validScores
                .sort((a, b) => (b.score || 0) - (a.score || 0))
                .slice(0, 10);
            
            // Populate the table
            const tableBody = document.getElementById('highscore-list');
            if (tableBody) {
                tableBody.innerHTML = '';
                
                if (topScores.length === 0) {
                    tableBody.innerHTML = '<tr><td colspan="5" style="text-align: center; color: #888;">No high scores yet</td></tr>';
                } else {
                    topScores.forEach((score, index) => {
                        const row = document.createElement('tr');
                        row.innerHTML = `
                            <td>${index + 1}</td>
                            <td>${score.name || 'Player'}</td>
                            <td>${score.score || 0}</td>
                            <td>${score.level || 1}</td>
                            <td>${score.date || 'Unknown'}</td>
                        `;
                        tableBody.appendChild(row);
                    });
                }
            }
            
            // Hide loading and show table
            if (loadingDiv) loadingDiv.style.display = 'none';
            if (table) table.style.display = 'table';
            
        } catch (error) {
            console.log('Could not load high scores for display:', error);
            
            // Show error state
            if (loadingDiv) {
                loadingDiv.innerHTML = '❌ Error loading high scores';
                loadingDiv.style.color = '#f44';
            }
            
            const tableBody = document.getElementById('highscore-list');
            if (tableBody) {
                tableBody.innerHTML = '<tr><td colspan="5" style="text-align: center; color: #f44;">Error loading scores</td></tr>';
            }
            
            // Still show the table even with error
            if (table) table.style.display = 'table';
        }
    }
}

// ES6 export for Vite
export default GameManager;